import React, { FunctionComponent, useState } from "react";
import {
  StyleSheet,
  View,
  Image,
  TouchableHighlight,
  ActivityIndicator,
  Platform,
} from "react-native";
import { v4 as uuidv4 } from "react-native-uuid";
import { useActionSheet } from "@expo/react-native-action-sheet";
import * as Segment from "../../analytics";
import * as ExpoImagePicker from "expo-image-picker";
import * as Permissions from "expo-permissions";
import * as ImageManipulator from "expo-image-manipulator";
import { useApolloClient } from "@apollo/client";

import Heading from "../Heading";
import { uploadBlob } from "../../utils/presignedUpload";
import { compressImageWeb } from "../../utils/compressImageWeb";
import { colors } from "../../theme";
import { UploadResult } from "./types";
import { usePresignedImageUrl } from "../../hooks/usePresignedImageUrl";

type Props = {
  onUpload: (file: UploadResult) => void;
  value: string | null;
  blankMessage?: string;
};

const ImagePicker: FunctionComponent<Props> = ({
  onUpload,
  value,
  blankMessage = "Upload from image or take picture",
}) => {
  const client = useApolloClient();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const { showActionSheetWithOptions } = useActionSheet();
  const { url: previewUrl } = usePresignedImageUrl(value);

  const uploadFile = async (result: ExpoImagePicker.ImagePickerResult) => {
    if (result.cancelled) {
      return;
    }

    const fileName = uuidv4();

    try {
      setIsUploading(true);
      let sourceUri = result.uri;

      // ImageManipulator can produce a URI that fetch cannot read in Expo
      // web, so web uses a separate canvas-based path (compressImageWeb)
      // after reading the original blob below, instead of resizing here.
      if (Platform.OS !== "web") {
        const resizedImage = await ImageManipulator.manipulateAsync(
          result.uri,
          [{ resize: { width: result.width / 2, height: result.height / 2 } }],
          { compress: 0.8 },
        );
        sourceUri = resizedImage.uri;
      }

      const response = await fetch(sourceUri);
      if (!response.ok) {
        throw new Error(
          "The selected image could not be read (" + response.status + ")",
        );
      }
      let blob = await response.blob();
      if (Platform.OS === "web") {
        blob = await compressImageWeb(blob, 1600, 0.8);
      }
      const objectUrl = await uploadBlob(client, blob, "customer-id-images");
      onUpload({ name: fileName as string, ssl_url: objectUrl });
    } catch (error) {
      const detail = error instanceof Error ? error.message : String(error);
      console.error("ID image upload failed", error);
      alert("The image could not be uploaded: " + detail);
    } finally {
      setIsUploading(false);
    }
  };

  const onImagePickerSelect = async () => {
    if (Platform.OS !== "web") {
      const {
        status,
      } = await ExpoImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== "granted") {
        alert("Sorry, we need camera roll permissions to make this work!");
      }
    }

    Segment.trackWithProperties("Camera Roll Launched", {});

    const result = await ExpoImagePicker.launchImageLibraryAsync({});
    uploadFile(result);
  };

  const openActionSheet = () => {
    // TODO: Finish the action sheet
    const options = ["Take new picture", "Select existing image", "Cancel"];
    const cancelButtonIndex = 2;

    showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex,
      },
      async (idx) => {
        switch (idx) {
          case 0: {
            const { status } = await Permissions.askAsync(Permissions.CAMERA);

            Segment.trackWithProperties("Camera Launched", {
              permissionStatus: status,
            });

            if (status === "granted") {
              const result = await ExpoImagePicker.launchCameraAsync();
              uploadFile(result);
            } else {
              alert("You must allow access to access this feature");
            }
            break;
          }
          case 1: {
            await onImagePickerSelect();
            break;
          }
          default: {
            return;
          }
        }
      },
    );
  };

  return (
    <View style={styles.container}>
      {!value && !isUploading && (
        <TouchableHighlight
          style={styles.blankState__Button}
          onPress={
            Platform.OS === "web" ? onImagePickerSelect : openActionSheet
          }
        >
          <View style={styles.blankState}>
            <Image
              source={require("../../../assets/icons/camera.png")}
              style={{ width: 60, height: 50 }}
            />
            <Heading size="smallcaps" style={styles.blankState__Text}>
              {blankMessage}
            </Heading>
          </View>
        </TouchableHighlight>
      )}

      {value && previewUrl && (
        <View>
          <Image source={{ uri: previewUrl }} style={styles.image} />

          <TouchableHighlight
            style={styles.existingImage__Button}
            onPress={
              Platform.OS === "web" ? onImagePickerSelect : openActionSheet
            }
          >
            <View style={styles.existingImage}>
              <Image
                source={require("../../../assets/icons/camera.png")}
                style={styles.existingImage__Icon}
              />
            </View>
          </TouchableHighlight>
        </View>
      )}

      {isUploading && (
        <View style={styles.loading}>
          <ActivityIndicator
            color={colors.yellow}
            style={{ marginBottom: 10 }}
          />
          <Heading size="smallcaps" style={{ color: colors.white }}>
            Uploading...
          </Heading>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.darkestGrey,
    borderRadius: 20,
    overflow: "hidden",
    height: 365,
    marginTop: 20,
    marginBottom: 20,
    position: "relative",
  },
  blankState: {
    backgroundColor: colors.yellow,
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  blankState__Button: {
    flex: 1,
    height: 365,
    width: "100%",
    backgroundColor: colors.yellow,
  },
  blankState__Text: {
    marginTop: 20,
    marginHorizontal: 80,
    textAlign: "center",
  },
  loading: {
    backgroundColor: colors.black,
    width: 150,
    height: 80,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    left: "50%",
    top: "50%",
    marginLeft: -75,
    marginTop: -40,
  },
  image: {
    height: 365,
  },
  existingImage: {
    backgroundColor: colors.yellow,
    width: 50,
    height: 50,
    alignItems: "center",
    justifyContent: "center",
  },
  existingImage__Button: {
    backgroundColor: colors.yellow,
    borderRadius: 50,
    overflow: "hidden",
    position: "absolute",
    right: 20,
    top: 20,
  },
  existingImage__Icon: {
    width: 24,
    height: 20,
  },
});

export default ImagePicker;
